
/* eslint-disable @typescript-eslint/camelcase */
import {
  HangingIncoming,
  FollowUpGroup,
  AWB,
  FollowUpSTT
} from "@/domain/entities/HangingIncoming";
import { Options, Vue, prop } from "vue-class-component";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Checkbox from "primevue/checkbox";
import whatsapp from "@/app/infrastructures/misc/common-library/Whatsapp";
import formatDate from "@/app/infrastructures/misc/common-library/v2/FormatDate";
import { SubmitHangingConfirmationFollowUp } from "@/data/payload/api/HangingApiRequest";
import ConfirmAction from "../../../components/ConfirmAction.vue";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { SttHangingController } from "@/app/ui/controllers/SttHangingController";
import { AccountController } from "@/app/ui/controllers/AccountController";

class Props {
  modelValue = prop<HangingIncoming[]>({
    default: () => [],
    required: true
  });
  loading = prop<boolean>({
    default: false,
    type: Boolean
  });
  skeletonCount = prop<number>({
    default: 3,
    type: Number
  });
}

@Options({
  components: {
    Accordion,
    AccordionTab,
    Checkbox,
    ConfirmAction
  },
  emits: ["update:modelValue", "template", "success", "download", "done"]
})
class CardCollipsableIncoming extends Vue.with(Props) {
  isOpenModal = false;
  lastConfirmData = new HangingIncoming();

  mapActivityType(type: HangingIncoming) {
    const label: any = {
      follow_up_airport: this.$t("dashboard.hanging.followUpAirport"),
      follow_up_courier_truck: `${this.$t("dashboard.hanging.followUpCourierTruck")} ${type.followUp.courierName}`,
      follow_up_checker: `${this.$t("dashboard.hanging.followUpChecker")} ${type.followUp.username}`,
      follow_up_warehouse: this.$t("dashboard.hanging.searchSttWarehouse"),
      follow_up_konsol: `${this.$t("dashboard.hanging.followUpConsole")} ${type.followUp.origin3lc} - ${type.followUp.originName}`
    };

    const icon: any = {
      follow_up_airport: "ic_plane_hanging",
      follow_up_courier_truck: "ic_truck_hanging",
      follow_up_checker: "ic_checker_hanging",
      follow_up_warehouse: "ic_search_hanging",
      follow_up_konsol: "ic_console_hanging"
    };

    return {
      label: label[type.activityType] ?? this.$t("dashboard.hanging.followUpAirport"),
      icon: icon[type.activityType] ?? "ic_plane_hanging"
    };
  }

  mapFollowUp(type: HangingIncoming) {
    const label: any = {
      belum:
        type.activityType === "follow_up_warehouse"
          ? this.$t("dashboard.cardCollapsibleHanging.notSearched")
          : this.$t("dashboard.hanging.notFollowedUp"),
      sudah:
        type.activityType === "follow_up_warehouse"
          ? this.$t("dashboard.cardCollapsibleHanging.doneSearchStt")
          : this.$t("dashboard.hanging.followUpDone")
    };

    const icon: any = {
      belum:
        type.activityType === "follow_up_airport" ? "clock-red" : "warning-red",
      sudah: "check-circle"
    };

    const color: any = {
      belum: "text-red-lp-2000",
      sudah: "text-green-lp-100"
    };

    return {
      label: label[type.followUpStatus] ?? this.$t("dashboard.hanging.notFollowedUp"),
      icon: icon[type.followUpStatus] ?? "warning-red",
      color: color[type.followUpStatus] ?? "text-red-lp-2000"
    };
  }

  mapHangingDays(stt: FollowUpSTT) {
    const colorMap: Record<number, string> = {
      1: "bg-aqua-lp-200 text-aqua-lp-500",
      2: "bg-aqua-lp-200 text-aqua-lp-500",
      3: "bg-aqua-lp-200 text-aqua-lp-500",
      4: "bg-aqua-lp-200 text-aqua-lp-500",
      5: "bg-yellow-lp-200 text-yellow-lp-100",
      6: "bg-orange-lp-100 text-orange-lp-200"
    };

    let color = "bg-white text-gray-lp-300";

    if (stt.hangingDays != null) {
      if (colorMap[stt.hangingDays]) {
        color = colorMap[stt.hangingDays];
      } else if (stt.hangingDays >= 7) {
        color = "bg-red-lp-2000 text-white";
      }
    }

    const label =
      stt.hangingDays != null
        ? stt.hangingDays < 7
          ? `${stt.hangingDays} ${this.$t('dashboard.hanging.dayUnit')}`
          : this.$t('dashboard.hanging.day7Plus')
        : "-";

    return { label, color };
  }

  mapTicketStatus(stt: FollowUpSTT) {
    const label: any = {
      belum_ditemukan: this.$t('dashboard.hanging.statusNotFound'),
      ditemukan_konsol: this.$t('dashboard.hanging.statusFoundConsole'),
      ditemukan_pos: this.$t('dashboard.hanging.statusFoundPos')
    };

    const color: any = {
      belum_ditemukan: "bg-pink-lp-200 text-red-lp-100",
      ditemukan_konsol: "bg-blue-lp-600 text-blue-lp-700",
      ditemukan_pos: "bg-purple-lp-100 text-purple-lp-800"
    };

    return {
      label: label[stt.existenceStatus] ?? "-",
      color: color[stt.existenceStatus] ?? ""
    };
  }

  isFollowUpDone(type: HangingIncoming) {
    return type.followUpStatus === "sudah";
  }

  onClickTemplate(data: HangingIncoming) {
    this.$emit("template", data);
  }

  onConfirmDone(data: HangingIncoming) {
    this.lastConfirmData = data;
    this.isOpenModal = true;
  }

  onClickWhatsapp(item: HangingIncoming) {
    const cityCode = this.accountData.account_type_detail.partnerLocation?.city?.code;
    const group = item.groups;
    const groupName = group.flatMap(item => item.groupName).join(", ");
    const groupTotalPiece = group.reduce((sum, item) => sum + item.summary.totalPiece, 0);
    const awb = group.flatMap((group) => group.awbs);
    const awbList = awb
      .map((awbItem) => `${awbItem.awbNo} - Asal ${awbItem.origin3lc}`)
      .join('\n');

    const templateMessage =
      `${this.$t("dashboard.hanging.templateIntro")} ${this.$t("dashboard.hanging.accountTypeKonsolidator")} ${this.$t("dashboard.hanging.templateLionParcel")} ${cityCode}.\n` +
      `${this.$t("dashboard.hanging.templateHangingAwb", { groupName })} ${this.$t("dashboard.hanging.templateMoreThan3Days")}.\n` +
      `${this.$t("dashboard.hanging.templateTotalAwb", { totalAwb: awb.length, totalPiece: groupTotalPiece })}\n` +
      `${awbList}\n` +
      `${this.$t("dashboard.hanging.templateHelpQuestionAwb")} ${this.$t("dashboard.hanging.templateThanks")}.`;
    whatsapp(item.followUp.courierPhone, templateMessage);
  }

  onDownload(item: HangingIncoming) {
    const listStt = item.groups
      .flatMap((group) => group.awbs)
      .flatMap((awb) => awb.stts)
      .map((stt) => stt.sttNo);
    this.$emit("download", listStt);
  }

  onSearch() {
    this.$router.push("/cari-stt/scan");
  }

  onTabOpen(item: HangingIncoming) {
    item.isCollapsed = true;
    this.$emit("update:modelValue", this.modelValue);
  }

  onTabClose(item: HangingIncoming) {
    item.isCollapsed = false;
    this.$emit("update:modelValue", this.modelValue);
  }

  onUpdateGroup(item: FollowUpGroup) {
    item.isChecked = !item.isChecked;
    item.awbs.forEach((awb) => {
      awb.isChecked = item.isChecked;
      awb.stts.forEach((stt) => (stt.isChecked = item.isChecked));
    });
    this.$emit("update:modelValue", this.modelValue);
  }

  onUpdateAwb(item: AWB, group: FollowUpGroup) {
    item.isChecked = !item.isChecked;
    item.stts.forEach((stt) => (stt.isChecked = item.isChecked));
    group.isChecked = group.awbs.every((awb) => awb.isChecked);
    this.$emit("update:modelValue", this.modelValue);
  }

  onUpdateStt(stt: FollowUpSTT, awb: AWB, group: FollowUpGroup) {
    stt.isChecked = !stt.isChecked;
    awb.isChecked = awb.stts.every((awbItem) => awbItem.isChecked);
    group.isChecked = group.awbs.every((groupItem) => groupItem.isChecked);
    this.$emit("update:modelValue", this.modelValue);
  }

  // Format Date
  formatDateToString(date: string) {
    return formatDate(date);
  }

  async onRequestConfirmation() {
    try {
      this.isOpenModal = false;
      MainAppController.showLoading();
      const payload = new SubmitHangingConfirmationFollowUp({
        activityType: this.lastConfirmData.activityType,
        courierName: this.lastConfirmData.activityType === "follow_up_courier_truck" ? this.lastConfirmData.followUp?.courierName : "",
        courierPhone: this.lastConfirmData.activityType === "follow_up_courier_truck" ? this.lastConfirmData.followUp?.courierPhone : "",
        checkerUsername: this.lastConfirmData.activityType === "follow_up_checker" ? this.lastConfirmData.followUp?.username : "",
        origin3lc: this.lastConfirmData.activityType === "follow_up_konsol" ? this.lastConfirmData.followUp?.origin3lc : "",
        originName: this.lastConfirmData.activityType === "follow_up_konsol" ? this.lastConfirmData.followUp?.originName : ""
      })
      await SttHangingController.submitHangingConfirmationFollowUp(payload)
      MainAppController.setSnackbarMessage(`<div class="font-bold">${this.$t("dashboard.hanging.successUpdate")}</div>`);
      MainAppController.setSnackbarPositivity(true);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbar(true);
      this.$emit("success");
    } catch (err) {
      MainAppController.setSnackbarMessage(
        `<div class="font-bold">${this.$t("dashboard.hanging.failUpdate")}</div>`
      );
      MainAppController.setSnackbarPositivity(false);
      MainAppController.setSnackbarTimeout(2000);
      MainAppController.setSnackbar(true);
    } finally {
      MainAppController.closeLoading();
    }
  }

  get accountData() {
    return AccountController.accountData;
  }
}
export default CardCollipsableIncoming;
